import { ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/use-auth";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/layout/route-states";

type AuthGateProps = {
  children: ReactNode;
};

export function AuthGate({ children }: AuthGateProps) {
  const auth = useAuth();
  const { t } = useTranslation("auth");
  const common = useTranslation("common").t;

  if (auth.status === "loading") {
    return <LoadingState />;
  }
  if (auth.status === "unavailable") {
    return <AuthMessage title={t("unavailable")} detail={t("unavailableDetail")} />;
  }
  if (auth.status === "anonymous") {
    return (
      <AuthMessage
        title={t("signedOut")}
        detail={t("signedOutDetail")}
        action={<Button onClick={() => auth.login()}>{common("actions.signIn")}</Button>}
      />
    );
  }
  if (auth.status === "forbidden") {
    return <AuthMessage title={t("forbidden")} detail={t("forbiddenDetail")} action={<Button variant="outline" onClick={auth.logout}>{common("actions.signOut")}</Button>} />;
  }
  return children;
}

function AuthMessage({ title, detail, action }: { title: string; detail: string; action?: ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <div className="w-full max-w-md rounded-md border bg-panel p-6 text-panel-foreground shadow-sm">
        <ShieldAlert className="h-8 w-8 text-primary" />
        <h1 className="mt-4 text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{detail}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}
